import { type FC, useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { gql, useQuery, useMutation } from '@apollo/client';

import {
  OrderArchiveState,
  GenerateOrderArchiveMutation,
  GenerateOrderArchiveMutationVariables,
  OrderArchiveQuery,
  OrderArchiveQueryVariables,
} from 'client/graphql/types/operations';

import Button from './Button';

const ORDER_ARCHIVE_QUERY = gql`
  query OrderArchiveQuery($id: Int!) {
    orderArchive(id: $id) {
      id
      state
      fileUrl
    }
  }
`;

const GENERATE_ORDER_ARCHIVE_MUTATION = gql`
  mutation GenerateOrderArchiveMutation($input: GenerateOrderArchiveInput!) {
    generateOrderArchive(input: $input) {
      success

      orderArchive {
        id
        state
        fileUrl
      }
    }
  }
`;

interface Props {
  archiveId: number;
}

const POLL_INTERVAL = 2500;

const ArchiveDownloadButton: FC<Props> = (props) => {
  const { archiveId } = props;

  const { t } = useTranslation();

  const [generateOrderArchive] = useMutation<
    GenerateOrderArchiveMutation,
    GenerateOrderArchiveMutationVariables
  >(GENERATE_ORDER_ARCHIVE_MUTATION, {
    update: (cache, result) => {
      if (result.data?.generateOrderArchive?.success) {
        cache.writeQuery<OrderArchiveQuery>({
          query: ORDER_ARCHIVE_QUERY,
          data: {
            __typename: 'Query',
            orderArchive: result.data.generateOrderArchive.orderArchive,
          },
        });
      }
    },
  });

  const { data, loading, startPolling, stopPolling } = useQuery<
    OrderArchiveQuery,
    OrderArchiveQueryVariables
  >(ORDER_ARCHIVE_QUERY, {
    variables: { id: archiveId },
  });

  const [downloading, setDownloading] = useState(false);

  const orderArchiveState = data?.orderArchive?.state;

  useEffect(() => {
    if (
      downloading &&
      orderArchiveState === OrderArchiveState.Pending &&
      data?.orderArchive?.id != null
    ) {
      generateOrderArchive({
        variables: { input: { orderArchiveId: data.orderArchive.id } },
      });
    }
  }, [
    downloading,
    orderArchiveState,
    data?.orderArchive?.id,
    generateOrderArchive,
  ]);

  useEffect(() => {
    if (orderArchiveState === OrderArchiveState.Generating) {
      startPolling(POLL_INTERVAL);
    } else if (
      orderArchiveState === OrderArchiveState.Completed ||
      orderArchiveState === OrderArchiveState.Failed
    ) {
      stopPolling();
    }
  }, [orderArchiveState, startPolling, stopPolling]);

  const handleDownload = () => {
    setDownloading(true);
  };

  let translationKey: string;

  if (orderArchiveState === OrderArchiveState.Generating) {
    translationKey = 'orderArchive.generating';
  } else if (downloading) {
    translationKey = 'orderArchive.downloading';
  } else {
    translationKey = 'orderArchive.download';
  }

  useEffect((): (() => void) | void => {
    if (downloading && data?.orderArchive?.fileUrl != null) {
      const timeout = setTimeout(() => {
        setDownloading(false);
      }, 2000);

      return () => {
        clearInterval(timeout);
      };
    }
  }, [downloading, data?.orderArchive?.fileUrl]);

  return (
    <>
      <Button
        onClick={handleDownload}
        disabled={
          loading ||
          downloading ||
          orderArchiveState === OrderArchiveState.Generating
        }
      >
        {t(translationKey)}
      </Button>

      {downloading && data?.orderArchive?.fileUrl != null && (
        <iframe
          src={data.orderArchive.fileUrl}
          style={{ width: 0, height: 0, margin: 0, border: 'none' }}
        />
      )}
    </>
  );
};

export default ArchiveDownloadButton;
